import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { CreateAndEditDistributionPlan } from './DistributionPlan/CreateAndEditDistributionPlan'
import { type ApplyFiltersAndDispatchParams, type Row } from '../../components/CustomTable/types'
import ROUTES from '../../consts/paths'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DeleteDistributionPlan from './DistributionPlan/DeleteDistributionPlan'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import PAGE_TITLES from '../../consts/titles'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import { useQuery } from '@tanstack/react-query'
import { DistributionManager } from '../../APIManager'
import { notifyError } from '../../utils/notifyError'
import { type ProjectScopeEnum } from '../../pages/ProjectPage'
import ReportPermissionsFeature from '../ReportPermissionsFeature'
import ResultsThresholdFeature from '../ResultsThresholdFeature'
import GradePostingInstructorReleaseModal from './GradePostingInstructorReleaseModal'
import InstructorSurveyDateChangeModal from './InstructorSurveyDateChangeModal'
import { type DropdownItemConfig } from '../../components/Dropdown'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import CustomTooltip from '../../components/CustomTooltip'
import { SANDBOX_TOOLTIP } from '../../components/SandboxBadge'
import LeadText from '../../components/CustomTable/CellTypes/LeadText'
import { yellow } from '../../colors'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

interface DistributionPlanTableProps {
  scope: ProjectScopeEnum
  /** Series-level edit permission (drives read-only mode for the report-permissions modal). */
  canEditReportPermissions?: boolean
  /** Selected series title, shown in the report-permissions modal subtitle. */
  seriesTitle?: string
  /**
   * Current value of the series' instructor-date-change setting (CR-337).
   *
   * Passed down rather than fetched here: `ProjectPage` already holds the series list this page
   * is scoped to, so the menu toggle costs no extra request on a table that already loads a
   * paginated list. Absent → Off.
   */
  isInstructorSurveyDateChangeEnabled?: boolean
}

export const DistributionPlanTable: React.FC<DistributionPlanTableProps> = ({
  scope,
  canEditReportPermissions = false,
  seriesTitle,
  isInstructorSurveyDateChangeEnabled = false
}) => {
  const navigate = useNavigate()
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageSize: 10 })
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId') ?? ''
  const [showReportPermissions, setShowReportPermissions] = useState<boolean>(false)
  const [showResultsThreshold, setShowResultsThreshold] = useState<boolean>(false)
  const [showGradePostingRelease, setShowGradePostingRelease] = useState<boolean>(false)
  const [showInstructorDateChange, setShowInstructorDateChange] = useState<boolean>(false)

  useEffect(() => {
    setParams({ pageSize: 10 })
  }, [projectId])

  const {
    data: distributionPlan = [],
    isLoading,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ['distributionPlan', projectId, params, scope],
    queryFn: async () => {
      return (await DistributionManager.getDistributionById(projectId, params)).data
    },
    enabled: !!projectId,
    retry: false
  })

  const totalResults = Number(
    distributionPlan?.pagination?.total_results ??
    distributionPlan?.pagination?.totalResults ??
    0
  )
  const hasExistingProjectTerms = totalResults > 0

  /**
   * A rehearsal, marked on the term itself (CR-341, per-term since the backend's `80a0e6006`).
   *
   * An icon before the term name rather than a chip after the status: the row already carries a
   * status chip, and a second chip beside it reads as a second status. It follows the marker
   * idiom this table already has — `CellWarning`'s amber `WarningAmber` and red `ErrorOutline`,
   * both outlined, 22px, tooltipped — so `ScienceOutlined` in the same amber as the warning
   * marker and the `Sandbox` chip elsewhere. A flask says *trial run*, which is the word the
   * backend's own spec uses; an eye-with-a-slash would say only *hidden*, which is a third of it.
   *
   * `mr: 1` where `CellWarning` uses `mr: 3` — matching the *gap*, not the number. This theme's
   * spacing is `0.25 * f` rem, so those are 4px and 12px; the warning triangle fills its 22px box
   * edge to edge while the flask is a narrow glyph with empty box either side, and its optical
   * gap is the margin plus that inset. Copying the 12px put visible daylight between the marker
   * and the term it belongs to.
   *
   * It has to be an override on an existing column, because `is_sandbox` reaches this table as
   * row data and never as a column (`SlotCDGSchema` dumps it; `DISTRIBUTION_KEYS` declares five
   * columns), and `overrides` fire per rendered column.
   *
   * Overriding replaces the cell, so the first column's own rendering is reproduced here:
   * `LeadText` exactly as `generateCells` builds it for `FieldType.LINKED_ENTITY`. The one thing
   * it does not reproduce is `CellWarning` — the terms list carries no `warning_message` at all
   * (that field is on `slot_node_schema`, the Course Sections rows), so there is nothing to drop.
   *
   * The row handed over is `flattenKeys(data)`: dotted leaf keys, no nested objects. Memoised
   * because it feeds `generateColumns`'s own `useMemo`.
   */
  const termCellOverride = useMemo(() => ({
    'term.termName': (row: Record<string, unknown>) => (
      <Box display='flex' alignItems='center'>
        {row?.isSandbox === true && (
          <CustomTooltip title={SANDBOX_TOOLTIP} placement='top'>
            <ScienceOutlinedIcon sx={{ color: yellow[150], fontSize: 22, mr: 1 }} />
          </CustomTooltip>
        )}
        <LeadText sx={{ cursor: 'pointer', whiteSpace: 'pre' }}>{row?.['term.termName'] as string}</LeadText>
      </Box>
    )
  }), [])

  // CR-341 wanted a 404 here — a sandboxed project filtered out of the caller's list whose id is
  // still in a bookmark — to render Not Found instead of toasting. Reverted 2026-09-18: the page
  // it rendered is a *full-page* layout (`NotFoundFeature` → `CenteredContainer`, a centred
  // `Container` at `calc(100vh - …)`), and this table is the right-hand pane of a two-column
  // page, so it broke the layout and put the sidebar over the table. The behaviour also had no
  // sign-off (spec's Approved Behavior Change #4). If it comes back it needs an in-pane empty
  // state, not a page.
  useEffect(() => {
    if (isError) notifyError(error, { fallbackMessage: 'Failed to load Survey Project instances. Please try again.' })
  }, [isError])

  // Grouped under sub-headers, the same shape as the Course Sections table's menu: every row here
  // edits series-level report config (SurveyProject.EDIT), so the whole menu — headers included —
  // is hidden for read-only roles rather than opening a disabled modal.
  const seriesActionsMenu: DropdownItemConfig[] = [
    // ── REPORTS & RESULTS ────────────────────────────────────────────────────
    { text: 'REPORTS & RESULTS', isHeader: true, visible: canEditReportPermissions },

    { text: 'Edit Report Permissions', action: () => { setShowReportPermissions(true) }, visible: canEditReportPermissions },

    { text: 'Edit Results Threshold', action: () => { setShowResultsThreshold(true) }, visible: canEditReportPermissions },

    { text: 'Release Instructor Results on Grade Posting', action: () => { setShowGradePostingRelease(true) }, visible: canEditReportPermissions },

    // ── divider ──────────────────────────────────────────────────────────────
    { text: '', isDivider: true, visible: canEditReportPermissions },

    // ── INSTRUCTOR SETTINGS ──────────────────────────────────────────────────
    { text: 'INSTRUCTOR SETTINGS', isHeader: true, visible: canEditReportPermissions },

    {
      // On/Off chip plus a modal, matching Sync Course Sections: the row reports the current
      // state at a glance, and the change is confirmed in the modal rather than on the click.
      text: 'Allow Instructors to Change Survey Dates',
      action: () => { setShowInstructorDateChange(true) },
      chip: {
        label: isInstructorSurveyDateChangeEnabled ? 'On' : 'Off',
        color: isInstructorSurveyDateChangeEnabled ? 'success' : 'default'
      },
      visible: canEditReportPermissions
    }
  ]

  return (<Box display='flex' flexDirection='column' gap={6} width={'100%'}>

      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.DISTRIBUTION_SERIES} />}>
        <AppTable
          key={projectId}
          /**
           * The term's own sandbox flag, badged beside its status (CR-341, per-term since the
           * backend's `80a0e6006`).
           *
           * It rides the **Term** cell rather than a cell of its own, because `is_sandbox`
           * reaches this table as row data but not as a column: `SlotCDGSchema` dumps it while
           * `DISTRIBUTION_KEYS` declares five columns — Term, Survey Template, Course Sections,
           * Enrollments, Status — and `overrides` only fires for a column that renders. Keyed on
           * `isSandbox` it matched nothing and drew nothing, silently.
           *
           * The Status cell was the first host and is no longer: overriding a cell replaces it,
           * so badging there meant rebuilding the status chip by hand, and the rebuild read
           * `slotNodeDetails.slotStatus` as a nested object when `generateCell` hands over
           * `flattenKeys(data)` — dotted keys, no nesting — which left every row with no status
           * chip at all. The Term cell needs no such reconstruction: the icon sits beside the
           * name the row already prints.
           *
           * The list also omits sandboxed terms entirely for a caller holding no Global-level
           * role, before pagination, so a row that arrives is one its reader may see.
           */
          overrides={termCellOverride}
          createAction={<CreateAndEditDistributionPlan projectId={projectId} shouldLoadLatestDefaults={hasExistingProjectTerms} />}
          list={distributionPlan ?? []}
          loading={isLoading || isFetching}
          deleteAction={<DeleteDistributionPlan seriesId={distributionPlan?.distributionSeriesId} />}
          topbarActionsDropdownMenu={projectId !== '' ? seriesActionsMenu : undefined}
          createActionTitle='Add Term'
          actionModalEntityDisplayField={'term.termName'}
          createButtonProps={{
            buttonType: ButtonType.Secondary,
            buttonSize: ButtonSize.Medium,
            disabled: !projectId
          }}
          createModalProps={{
            showCloseIcon: true
          }}
          featureName="Survey Project"
          onEditClick={(selectedRowData: Row) => {
            navigate(`${ROUTES.COURSE_AND_ENROLLMENT}/${selectedRowData?.data?.slotId as string}?projectId=${projectId}`)
          }}
          params={params}
          setParams={setParams}
          noDataComponentProps={{
            title: 'Start by adding a term',
            subTitle: 'Plan, coordinate, and execute Surveys by creating a Survey Project.'
          }}
        />
      </Suspense>
      {showInstructorDateChange && (
        <InstructorSurveyDateChangeModal
          distributionSeriesId={projectId}
          seriesTitle={seriesTitle}
          isEnabled={isInstructorSurveyDateChangeEnabled}
          canEdit={canEditReportPermissions}
          isOpen={showInstructorDateChange}
          handleCloseModal={() => { setShowInstructorDateChange(false) }}
        />
      )}
      {showGradePostingRelease && (
        <GradePostingInstructorReleaseModal
          distributionSeriesId={projectId}
          seriesTitle={seriesTitle}
          canEdit={canEditReportPermissions}
          isOpen={showGradePostingRelease}
          handleCloseModal={() => { setShowGradePostingRelease(false) }}
        />
      )}
      {showReportPermissions && (
        <ReportPermissionsFeature
          scope="series"
          scopeId={projectId}
          scopeLabel={seriesTitle}
          canEdit={canEditReportPermissions}
          isOpen={showReportPermissions}
          handleCloseModal={() => { setShowReportPermissions(false) }}
        />
      )}
      {showResultsThreshold && (
        <ResultsThresholdFeature
          seriesId={projectId}
          scopeLabel={seriesTitle}
          canEdit={canEditReportPermissions}
          isOpen={showResultsThreshold}
          handleCloseModal={() => { setShowResultsThreshold(false) }}
        />
      )}
      </Box>
  )
}
